import { getDrugByCui, getDrugByName } from "../../../Pfizer/form/formSections/prescription/drugDefinitions";
import { translatePlanMarket } from "../../../../../../constant/planMarkets";
import { UNSPECIFIED_MEMBER_ID, UNSPECIFIED_RX_MEMBER_ID } from "../../../../../../constant/coverages";

const brandNameToIcdMap = {
  BESPONSA: ["C91"],
  BOSULIF: ["C92"],
  BRAFTOVI: ["C43", "C18", "C34"],
  DAURISMO: ["C92"],
  IBRANCE: ["C50"],
  INLYTA: ["C64"],
  LORBRENA: ["C34"],
  MEKTOVI: ["C43"],
  MYLOTARG: ["C92"],
  TALZENNA: ["C50", "C61"],
  TUKYSA: ["C50", "C18"],
  VIZIMPRO: ["C34"],
  XALKORI: ["C34", "C84", "D48"]
};

const getValidInsuranceTypeOptions = () => [
  "commercial",
  "medicare_part_d",
  "medicare_advantage",
  "medicare_part_a_and_b",
  "medicaid",
  "va",
  "other",
  "none"
];

const mapInsuranceType = (insuranceSection) => {
  if (!insuranceSection) {
    return { type: null, otherTypeName: null };
  }

  const { type: originalType, otherTypeName: existingOtherTypeName } = insuranceSection;
  const validOptions = getValidInsuranceTypeOptions();

  if (!originalType && existingOtherTypeName) {
    return { type: "other", otherTypeName: existingOtherTypeName };
  }

  if (originalType === "other") {
    return { type: "other", otherTypeName: existingOtherTypeName || null };
  }

  if (originalType && validOptions.includes(originalType)) {
    return { type: originalType, otherTypeName: null };
  }

  if (originalType && !validOptions.includes(originalType)) {
    const fallbackTypeName = existingOtherTypeName || translatePlanMarket(originalType) || originalType;
    return {
      type: "other",
      otherTypeName: fallbackTypeName
    };
  }

  return { type: null, otherTypeName: null };
};

const findCurrentDrugTreatment = (treatments, currentDrug) => {
  if (!treatments || !currentDrug) {
    return null;
  }

  const { manualMedications = [], importedTreatments = [], patientUncoveredDrugs = [] } = treatments;
  const allTreatments = [...manualMedications, ...importedTreatments];

  return allTreatments.find((treatment) => {
    const treatmentDrug = treatment.rxnormDrug;
    if (!treatmentDrug) {
      return false;
    }

    if (patientUncoveredDrugs.includes(treatmentDrug.id)) {
      return false;
    }

    const currentDrugName = currentDrug.name?.toLowerCase();
    const treatmentBnName = treatmentDrug.bnName?.toLowerCase();

    return currentDrugName === treatmentBnName;
  });
};

const getPrescriptionData = ({ treatment, isIV, initialValues, treatments, currentDrug }) => {
  if (!treatment) {
    return { ...initialValues };
  }

  const prescriptionData = {};

  if (isIV) {
    prescriptionData.vialSize = initialValues.vialSize || treatment.rxnormDrug?.strength;
    prescriptionData.numberOfVials = initialValues.numberOfVials || treatment?.dosageValue;
    prescriptionData.frequency =
      initialValues.frequency || treatment.frequency ? `${treatment.frequency} units per day` : undefined;
    prescriptionData.treatmentStartDate = initialValues.treatmentStartDate || treatment?.startDate;
  } else {
    prescriptionData.dosage = initialValues.dosage || extractNumericString(treatment.rxnormDrug?.strength);
    prescriptionData.quantity =
      initialValues.quantity || `${treatment.quantityValue / parseInt(treatment?.frequency || 1)}`;
  }
  prescriptionData.numberOfRefills = String(initialValues.numberOfRefills ?? treatment.numberOfRefillsAuthorized ?? "");

  const parts = [
    treatment.rxnormDrug?.dfName,
    treatment.dosageValue !== undefined && treatment.dosageUnit
      ? `${treatment.dosageValue}${treatment.dosageUnit}`
      : null,
    treatment.frequency ? `${treatment.frequency} units per day` : undefined
  ];

  prescriptionData.dosingInstructions = initialValues.dosingInstructions || parts.filter(Boolean).join(" ");

  const concomitantMedications = getConcomitantMedications(treatments, currentDrug);
  prescriptionData.concomitant = initialValues.concomitant || concomitantMedications || "None";

  return prescriptionData;
};

const getConcomitantMedications = (treatments, currentDrug) => {
  if (!treatments) {
    return "";
  }

  const { manualMedications = [], importedTreatments = [], patientUncoveredDrugs = [] } = treatments;
  const allTreatments = [...manualMedications, ...importedTreatments];

  const otherActiveMedications = allTreatments.filter((treatment) => {
    if (treatment.isHidden) {
      return false;
    }

    const treatmentDrug = treatment.rxnormDrug;
    if (!treatmentDrug) {
      return true;
    }

    if (patientUncoveredDrugs.includes(treatmentDrug.id)) {
      return false;
    }

    if (currentDrug) {
      const currentDrugName = currentDrug.name?.toLowerCase();
      const treatmentBnName = treatmentDrug.bnName?.toLowerCase();

      if (currentDrugName && treatmentBnName && currentDrugName === treatmentBnName) {
        return false;
      }
    }

    return true;
  });

  const brandNames = otherActiveMedications.map((treatment) => treatment.rxnormDrug?.bnName).filter(Boolean);

  return brandNames.join(", ");
};

export const getDrug = (prescription) => {
  if (!prescription) {
    return {};
  }

  const { drug, ttyCui, name } = prescription;
  if (drug) {
    return drug;
  }
  if (ttyCui) {
    return getDrugByCui(ttyCui);
  }
  if (name) {
    return getDrugByName(name);
  }
};

export const extractNumericString = (input) => {
  const match = input?.match(/[-+]?\d*\.?\d+/);
  return match ? match[0] : "";
};

const getMatchingTreatment = ({ treatments, currentDrug }) => {
  let matchingTreatment = null;

  if (treatments && currentDrug) {
    matchingTreatment = findCurrentDrugTreatment(treatments, currentDrug);
  }
  return matchingTreatment;
};
const getPrimaryDiagnosis = ({ diagnosis, currentDrug }) => {
  let primaryDiagnosis = null;

  if (diagnosis && diagnosis["patient-diagnoses"]) {
    const patientDiagnoses = diagnosis["patient-diagnoses"];
    if (currentDrug && currentDrug.name) {
      const drugName = currentDrug.name.toUpperCase();
      const matchingIcds = brandNameToIcdMap[drugName];
      if (matchingIcds && matchingIcds.length > 0) {
        const primaryDiagnosisList = patientDiagnoses.filter((diag) => diag.isPrimary);

        const matchingPrimaryDiagnosis = primaryDiagnosisList.find((diag) => {
          // Check if the patient's ICD code starts with any of the base codes in the map
          return matchingIcds.some((baseIcd) => diag.icd.startsWith(baseIcd));
        });

        if (matchingPrimaryDiagnosis) {
          primaryDiagnosis = matchingPrimaryDiagnosis.icd;
        }
      }
    }
  }

  return primaryDiagnosis;
};

export const buildPrescriptionSection = (initialValues, applicationForm, treatments, currentDrug) => {
  const matchingTreatment = getMatchingTreatment({ treatments, currentDrug });
  const treatmentPrescriptionData = getPrescriptionData({
    treatment: matchingTreatment,
    isIV: currentDrug.category === "iv",
    initialValues: initialValues.prescription,
    treatments,
    currentDrug
  });

  return {
    ...initialValues.prescription,
    ...treatmentPrescriptionData,
    drug: currentDrug,
    allergies:
      initialValues.prescription.allergies === undefined
        ? initialValues.prescription.allergies
        : !!initialValues.prescription.allergiesDetails
  };
};

export const buildInsuranceSection = (initialValues, applicationForm) => {
  const insuranceData = applicationForm.formData?.insurance;

  const medicalTypeMapping = mapInsuranceType(insuranceData?.medical);
  const primaryPrescriptionTypeMapping = mapInsuranceType(insuranceData?.primaryPrescription);
  const secondaryPrescriptionTypeMapping = mapInsuranceType(insuranceData?.secondaryPrescription);

  return {
    ...initialValues.insurance,
    medical: {
      ...initialValues.insurance.medical,
      memberId: initialValues?.insurance?.medical?.memberId?.startsWith(UNSPECIFIED_MEMBER_ID)
        ? null
        : initialValues?.insurance?.medical?.memberId,
      type: medicalTypeMapping.type,
      otherTypeName: medicalTypeMapping.otherTypeName
    },
    primaryPrescription: {
      ...initialValues.insurance.primaryPrescription,
      memberId: initialValues?.insurance?.primaryPrescription?.memberId?.startsWith(UNSPECIFIED_RX_MEMBER_ID)
        ? null
        : initialValues?.insurance?.primaryPrescription?.memberId,
      type: primaryPrescriptionTypeMapping.type,
      otherTypeName: primaryPrescriptionTypeMapping.otherTypeName
    },
    secondaryPrescription: {
      ...initialValues.insurance.secondaryPrescription,
      memberId: initialValues?.insurance?.secondaryPrescription?.memberId?.startsWith(UNSPECIFIED_RX_MEMBER_ID)
        ? null
        : initialValues?.insurance?.secondaryPrescription?.memberId,
      type: secondaryPrescriptionTypeMapping.type,
      otherTypeName: secondaryPrescriptionTypeMapping.otherTypeName
    }
  };
};

export const buildDiagnosisSection = (initialValues, diagnosis, currentDrug) => {
  const primaryDiagnosis = getPrimaryDiagnosis({ diagnosis, currentDrug });

  return {
    ...initialValues.diagnosis,
    primary: {
      icd10: initialValues.diagnosis?.primary?.icd10 || primaryDiagnosis
    }
  };
};
export const buildProviderSection = (initialValues) => {
  return {
    ...initialValues.provider,
    facilityContactName: initialValues.provider.facilityContactName
      ? initialValues.provider.facilityContactName
      : initialValues.provider.name
  };
};
